import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';
import websocketService from '../services/websocket';
import Navbar from '../components/Navbar';
import Sparkline from '../components/Sparkline';
import {
  TrendingUp,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  LogOut,
  Eye,
  EyeOff,
  Search,
  Star,
  Bell,
  CreditCard,
  ArrowRightLeft,
  Download,
  ChevronLeft,
  ChevronRight,
  Users,
  Gift,
  Copy
} from 'lucide-react';
import toast from 'react-hot-toast';

const DashboardPage = () => {
  const { user, logout } = useAuthStore();
  const [balances, setBalances] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [sparklineData, setSparklineData] = useState({});
  const [loading, setLoading] = useState(true);
  const [showBalance, setShowBalance] = useState(true);
  const [activeTab, setActiveTab] = useState('hot');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [referralStats, setReferralStats] = useState({ totalReferrals: 0, totalEarnings: 0 });
  const [referralSettings, setReferralSettings] = useState(null);
  const perPage = 50;

  const referralCode = user?.referralCode || user?.id?.slice(0, 8) || 'N/A';
  const referralLink = `${window.location.origin}/register?ref=${referralCode}`;

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      websocketService.connect(token);
    }

    fetchData();
    fetchReferralData();

    return () => {
      websocketService.disconnect();
    };
  }, []);

  const fetchReferralData = async () => {
    try {
      const [statsRes, settingsRes] = await Promise.all([
        api.get('/referrals/my-stats').catch(() => ({ data: { stats: {} } })),
        api.get('/referrals/settings').catch(() => ({ data: { settings: {} } }))
      ]);
      setReferralStats(statsRes.data.stats || {});
      setReferralSettings(settingsRes.data.settings || {});
    } catch (e) {
      console.log('Referral fetch error');
    }
  };

  const fetchData = async () => {
    try {
      const marketsRes = await api.get('/markets');
      const tradingPairs = marketsRes.data.tradingPairs || [];
      setMarkets(tradingPairs);
      setLoading(false);

      try {
        const balancesRes = await api.get('/wallet/balances');
        setBalances(balancesRes.data.balances || []);
      } catch (balanceError) {
        setBalances([]);
      }

      const symbols = tradingPairs.map(m => m.symbol).join(',');
      if (symbols) {
        api.get(`/markets/sparklines?symbols=${symbols}`).then(sparklinesRes => {
          setSparklineData(sparklinesRes.data.sparklineData || {});
        }).catch(err => { });
      }

      const symbolList = tradingPairs.map(m => m.symbol);
      if (symbolList.length > 0) {
        websocketService.subscribeToMarkets(symbolList);
      }

      const handleTickerUpdate = (data) => {
        setMarkets(prevMarkets => {
          const index = prevMarkets.findIndex(m => m.symbol === data.symbol);
          if (index === -1) return prevMarkets;

          const newMarkets = [...prevMarkets];
          if (newMarkets[index].lastPrice !== data.price) {
            newMarkets[index] = {
              ...newMarkets[index],
              lastPrice: data.price,
              priceChange24h: data.priceChangePercent,
              volume24h: data.volume,
              high24h: data.high,
              low24h: data.low
            };
          }
          return newMarkets;
        });
      };

      websocketService.on('ticker', handleTickerUpdate);
    } catch (error) {
      toast.error('Failed to load market data');
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
  };

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success('Referral link copied!');
  };

  const totalBalanceUSD = useMemo(() => {
    return balances.reduce((sum, b) => {
      const tokenSymbol = b.token?.symbol;
      if (!tokenSymbol) return sum;
      const tokenPrice = markets.find(m => m.baseToken?.symbol === tokenSymbol && m.quoteToken?.symbol === 'USDT')?.lastPrice || 0;
      const price = tokenSymbol === 'USDT' ? 1 : parseFloat(tokenPrice);
      return sum + (parseFloat(b.balance) * price);
    }, 0);
  }, [balances, markets]);

  const filteredMarkets = useMemo(() => {
    let filtered = markets;

    if (searchTerm) {
      filtered = filtered.filter(m => m.symbol.toLowerCase().includes(searchTerm.toLowerCase()));
    }

    switch (activeTab) {
      case 'gainers':
        return [...filtered].sort((a, b) => parseFloat(b.priceChange24h) - parseFloat(a.priceChange24h));
      case 'losers':
        return [...filtered].sort((a, b) => parseFloat(a.priceChange24h) - parseFloat(b.priceChange24h));
      case 'volume':
        return [...filtered].sort((a, b) => parseFloat(b.volume24h) - parseFloat(a.volume24h));
      case 'hot':
      default:
        return filtered;
    }
  }, [markets, activeTab, searchTerm]);

  // Pagination
  const totalPages = Math.ceil(filteredMarkets.length / perPage);
  const paginatedMarkets = filteredMarkets.slice((currentPage - 1) * perPage, currentPage * perPage);

  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm]);

  const getSparklineForSymbol = (symbol) => {
    if (sparklineData[symbol] && sparklineData[symbol].length > 0) {
      return sparklineData[symbol];
    }
    return null;
  };

  const totalCommission = referralSettings
    ? (referralSettings.level1Commission || 30) + (referralSettings.level2Commission || 20) + (referralSettings.level3Commission || 10)
    : 60;

  return (
    <div className="min-h-screen bg-[#0b0e11] text-gray-100 font-sans">
      <Navbar />

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2 bg-[#181a20] rounded-2xl p-6 border border-gray-800">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-6">
              <div>
                <div className="flex items-center gap-2 text-gray-400 mb-2">
                  <span>Estimated Balance</span>
                  <button onClick={() => setShowBalance(!showBalance)} className="hover:text-white">
                    {showBalance ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>
                </div>
                <div className="flex items-baseline gap-2">
                  <h2 className="text-3xl sm:text-4xl font-bold text-white">
                    {showBalance ? `$${totalBalanceUSD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '********'}
                  </h2>
                  <span className="text-gray-400">USD</span>
                </div>
                <div className="flex items-center gap-2 mt-2 text-green-400 text-sm font-medium">
                  <ArrowUpRight size={14} />
                  <span>+$1,240.50 (Today)</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                <Link to="/wallet" className="bg-[#fcd535] text-black px-4 py-2 sm:px-6 sm:py-2.5 rounded-lg font-semibold hover:bg-[#f0b90b] transition flex items-center gap-2 text-sm sm:text-base">
                  <Download size={18} /> Deposit
                </Link>
                <Link to="/wallet" className="bg-gray-700 text-white px-4 py-2 sm:px-6 sm:py-2.5 rounded-lg font-semibold hover:bg-gray-600 transition flex items-center gap-2 text-sm sm:text-base">
                  <CreditCard size={18} /> Withdraw
                </Link>
                <Link to="/wallet" className="bg-gray-700 text-white px-4 py-2 sm:px-6 sm:py-2.5 rounded-lg font-semibold hover:bg-gray-600 transition flex items-center gap-2 text-sm sm:text-base">
                  <ArrowRightLeft size={18} /> Transfer
                </Link>
              </div>
            </div>

            <div className="mt-8">
              <h3 className="text-lg font-semibold text-white mb-4">Your Assets</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {balances.filter(b => parseFloat(b.balance) > 0).slice(0, 4).map((balance) => (
                  <div key={balance.id} className="bg-[#0b0e11] p-4 rounded-xl border border-gray-800 hover:border-gray-700 transition">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center text-xs font-bold">
                        {balance.token?.symbol?.[0] || '?'}
                      </div>
                      <span className="font-semibold">{balance.token?.symbol || 'Unknown'}</span>
                    </div>
                    <div className="text-lg font-bold">
                      {showBalance ? parseFloat(balance.balance).toFixed(4) : '****'}
                    </div>
                    <div className="text-xs text-gray-400">
                      {balance.token?.name || 'Unknown Token'}
                    </div>
                  </div>
                ))}
                {balances.filter(b => parseFloat(b.balance) > 0).length === 0 && (
                  <div className="col-span-4 text-center py-8 text-gray-500">
                    <Wallet size={40} className="mx-auto mb-3 opacity-50" />
                    <p>No assets available</p>
                    <p className="text-xs mt-1">Deposit crypto to get started</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Refer & Earn - Dynamic */}
          <div className="bg-gradient-to-br from-blue-600 to-purple-700 rounded-2xl p-6 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl transform translate-x-10 -translate-y-10"></div>
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-3">
                <Gift className="text-yellow-300" size={24} />
                <span className="inline-block bg-white/20 px-3 py-1 rounded-full text-xs font-bold">EARN</span>
              </div>
              <h3 className="text-2xl font-bold mb-2">Refer & Earn</h3>
              <p className="text-blue-100 mb-4 text-sm">
                Invite friends and earn up to <span className="text-yellow-300 font-bold">{totalCommission}%</span> commission on their trades across 3 levels!
              </p>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-white/10 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-blue-200 text-xs mb-1">
                    <Users size={14} /> Referrals
                  </div>
                  <div className="text-xl font-bold">{referralStats.totalReferrals || 0}</div>
                </div>
                <div className="bg-white/10 rounded-lg p-3">
                  <div className="text-blue-200 text-xs mb-1">Earnings</div>
                  <div className="text-xl font-bold text-yellow-300">₹{(referralStats.totalEarnings || 0).toLocaleString()}</div>
                </div>
              </div>

              {/* Copy Link */}
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={referralLink}
                  readOnly
                  className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-xs font-mono truncate"
                />
                <button onClick={copyReferralLink} className="bg-white/20 hover:bg-white/30 p-2 rounded-lg transition" title="Copy Link">
                  <Copy size={18} />
                </button>
              </div>

              <Link to="/referrals" className="block text-center bg-white text-blue-600 px-6 py-2.5 rounded-lg font-bold hover:bg-gray-100 transition">
                View Referrals →
              </Link>
            </div>
          </div>
        </div>

        <div className="bg-[#181a20] rounded-2xl border border-gray-800 overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-gray-800 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-bold text-white">Market Overview</h2>
              <span className="text-gray-500 text-sm">({filteredMarkets.length} coins)</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 bg-[#0b0e11] px-3 py-1.5 rounded-lg">
                <Search size={14} className="text-gray-500" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bg-transparent border-none focus:outline-none w-32 text-white placeholder-gray-500 text-sm"
                />
              </div>
              <div className="flex gap-1 bg-[#0b0e11] p-1 rounded-lg overflow-x-auto">
                {['hot', 'gainers', 'losers', 'volume'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition whitespace-nowrap ${activeTab === tab ? 'bg-[#fcd535] text-black' : 'text-gray-400 hover:text-white'
                      }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-20 text-gray-500">Loading markets...</div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="block sm:hidden divide-y divide-gray-800">
                {paginatedMarkets.map((market) => (
                  <div key={market.id} className="p-4 hover:bg-[#2b3139] transition">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <img
                          src={`https://assets.coincap.io/assets/icons/${market.baseToken?.symbol.toLowerCase()}@2x.png`}
                          alt={market.baseToken?.symbol}
                          className="w-10 h-10 rounded-full"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://ui-avatars.com/api/?name=${market.baseToken?.symbol}&background=random&size=40`;
                          }}
                        />
                        <div>
                          <div className="font-bold text-white text-base">{market.baseToken?.symbol}</div>
                          <div className="text-xs text-gray-500">{market.baseToken?.name}</div>
                        </div>
                      </div>
                      <Link
                        to={`/trade/${market.symbol}`}
                        className="bg-[#fcd535] text-black px-4 py-1.5 rounded-lg font-semibold text-sm hover:bg-[#f0b90b] transition"
                      >
                        Trade
                      </Link>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-3 text-sm">
                      <div>
                        <div className="text-gray-500 text-xs mb-1">Price</div>
                        <div className="font-semibold text-white">
                          ${parseFloat(market.lastPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 text-xs mb-1">24h Change</div>
                        <div className={`font-semibold ${parseFloat(market.priceChange24h) >= 0 ? 'text-[#0ecb81]' : 'text-[#f6465d]'}`}>
                          {parseFloat(market.priceChange24h) >= 0 ? '+' : ''}{parseFloat(market.priceChange24h).toFixed(2)}%
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 text-xs mb-1">Volume</div>
                        <div className="font-semibold text-gray-300">
                          ${(parseFloat(market.volume24h) / 1000000).toFixed(2)}M
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop/Tablet Table View */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-gray-400 text-sm border-b border-gray-800">
                      <th className="px-6 py-4 font-medium">Name</th>
                      <th className="px-6 py-4 font-medium text-right">Price</th>
                      <th className="px-6 py-4 font-medium text-right">24h Change</th>
                      <th className="px-6 py-4 font-medium text-right">24h Volume</th>
                      <th className="px-6 py-4 font-medium text-right hidden md:table-cell">Market Cap</th>
                      <th className="px-6 py-4 font-medium text-center hidden lg:table-cell">Last 7 Days</th>
                      <th className="px-6 py-4 font-medium text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {paginatedMarkets.map((market) => {
                      const sparkline = getSparklineForSymbol(market.symbol);
                      return (
                        <tr key={market.id} className="hover:bg-[#2b3139] transition group">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <button className="text-gray-500 hover:text-[#fcd535]">
                                <Star size={16} />
                              </button>
                              <div className="flex items-center gap-2">
                                <img
                                  src={`https://assets.coincap.io/assets/icons/${market.baseToken?.symbol.toLowerCase()}@2x.png`}
                                  alt={market.baseToken?.symbol}
                                  className="w-8 h-8 rounded-full"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = `https://ui-avatars.com/api/?name=${market.baseToken?.symbol}&background=random&size=32`;
                                  }}
                                />
                                <div>
                                  <div className="font-bold text-white">{market.baseToken?.symbol}</div>
                                  <div className="text-xs text-gray-500">{market.baseToken?.name}</div>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="font-bold text-white">
                              ${parseFloat(market.lastPrice).toLocaleString('en-US', { minimumFractionDigits: market.priceDecimals })}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className={`font-medium ${parseFloat(market.priceChange24h) >= 0 ? 'text-[#0ecb81]' : 'text-[#f6465d]'}`}>
                              {parseFloat(market.priceChange24h) >= 0 ? '+' : ''}{parseFloat(market.priceChange24h).toFixed(2)}%
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right text-gray-300">
                            ${(parseFloat(market.volume24h) / 1000000).toFixed(2)}M
                          </td>
                          <td className="px-6 py-4 text-right text-gray-300 hidden md:table-cell">
                            ${(parseFloat(market.lastPrice) * 19000000).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                          </td>
                          <td className="px-6 py-4 hidden lg:table-cell">
                            <div className="flex justify-center">
                              {sparkline ? (
                                <Sparkline
                                  data={sparkline}
                                  color={parseFloat(market.priceChange24h) >= 0 ? '#0ecb81' : '#f6465d'}
                                  width={100}
                                  height={40}
                                />
                              ) : (
                                <div className="text-xs text-gray-600">Loading...</div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <Link
                              to={`/trade/${market.symbol}`}
                              className="text-[#fcd535] hover:text-[#f0b90b] font-medium text-sm"
                            >
                              Trade
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-800">
                  <div className="text-sm text-gray-400">
                    Showing {(currentPage - 1) * perPage + 1} - {Math.min(currentPage * perPage, filteredMarkets.length)} of {filteredMarkets.length}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft size={18} />
                    </button>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 py-1 rounded-lg text-sm font-medium ${currentPage === pageNum ? 'bg-[#fcd535] text-black' : 'bg-gray-800 text-gray-400 hover:text-white'}`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;

